import React from "react";
import * as yup from "yup";
import { message, Modal, Tooltip } from "antd";
import { Typography } from "@mui/material";
import { useTheme } from "../../ThemeContext";
import CustomContainer from "../common/CustomContainer";
import OForm from "../common/OForm";
import { createSsoOrg, deleteSsoOrg, getSsoOrg, getCustomerQuickLookup } from "../../utils/apiClient";
import { Button } from "@voicecare.ai/ui-library";
import { DeleteOutline } from "@mui/icons-material";

// Domain validation helper
const validateDomain = (domain) => {
  // Remove any whitespace
  const cleanDomain = domain.trim();
  
  // Basic domain format validation
  const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  // Additional checks
  if (!cleanDomain || cleanDomain.length === 0) return false;
  if (cleanDomain.length > 253) return false; // RFC 1035 limit
  if (cleanDomain.startsWith('.') || cleanDomain.endsWith('.')) return false;
  if (cleanDomain.includes('..')) return false; // No consecutive dots
  
  return domainRegex.test(cleanDomain);
};

const validateEmail = (email) => {
  // Remove any whitespace
  const cleanEmail = email.trim();
  
  // Basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  // Additional checks
  if (!cleanEmail || cleanEmail.length === 0) return false;
  if (cleanEmail.length > 254) return false; // RFC 5321 limit
  if (cleanEmail.includes('..')) return false; // No consecutive dots
  if (cleanEmail.startsWith('.') || cleanEmail.endsWith('.')) return false;
  
  return emailRegex.test(cleanEmail);
};

const validationSchema = yup.object().shape({
  orgName: yup.string().required("Organization Name is required"),
  domains: yup.array()
    .of(yup.string().test('domain-format', 'Invalid domain format', validateDomain))
    .min(1, "At least one domain is required"),
  itAdminEmail: yup.string()
    .test('email-format', 'Invalid email format', validateEmail)
    .required("IT Admin Email is required"),
});

const SsoSetup = () => {
  const { themeMode, currentTheme } = useTheme();
  const [ssoResponse, setSsoResponse] = React.useState(null);
  const [existingSsoData, setExistingSsoData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [planInfo, setPlanInfo] = React.useState(null);
  const [ssoAccessAllowed, setSsoAccessAllowed] = React.useState(false);

  const [formInitialValues] = React.useState({
    orgName: "",
    domains: [],
    itAdminEmail: "",
  });

  // Check customer plan and SSO access on component mount
  React.useEffect(() => {
    const checkCustomerAccess = async () => {
      try {
        // First check customer plan
        const customerStatus = await getCustomerQuickLookup();
        const { plan_name, has_active_subscription, subscription_status } = customerStatus;
        
        setPlanInfo({
          planName: plan_name,
          hasActiveSubscription: has_active_subscription,
          subscriptionStatus: subscription_status
        });

        // Check if customer is on Scale or Enterprise plan
        const isScaleOrEnterprise = plan_name && (
          plan_name.toLowerCase().includes('scale') || 
          plan_name.toLowerCase().includes('enterprise')
        );

        if (!isScaleOrEnterprise) {
          setSsoAccessAllowed(false);
          setLoading(false);
          return;
        }

        setSsoAccessAllowed(true);

        // If SSO is allowed, check existing SSO configuration
        const response = await getSsoOrg();
        if (response.statusCode === "SUCCESS") {
          setExistingSsoData(response.response);
          // If SAML exists and we have domain data, populate the response
          if (response.response.saml_exists && response.response.domains) {
            setSsoResponse(response.response);
          }
          // If WorkOS org no longer exists, show a warning
          if (!response.response.saml_exists && response.response.workos_org_id) {
            message.warning("Your previous WorkOS organization no longer exists. You can create a new SSO configuration.");
          }
        }
      } catch (error) {
        console.error("Error checking customer access:", error);
        // If there's an error, assume no SSO access
        setSsoAccessAllowed(false);
        setExistingSsoData({ saml_exists: false });
      } finally {
        setLoading(false);
      }
    };

    checkCustomerAccess();
  }, []);

  const handleDeleteOrg = async () => {
    setIsDeleting(true);
    try {
      const customerId = sessionStorage.getItem("customerId");
      const response = await deleteSsoOrg(customerId);
      if (response.statusCode === "SUCCESS") {
        message.success("SSO Organization deleted successfully!");
        setSsoResponse(null);
        setExistingSsoData({ saml_exists: false });
      } else {
        message.error(response.message || "Failed to delete SSO org.");
      }
    } catch (err) {
      message.error("Error deleting SSO org.");
    } finally {
      setIsDeleting(false);
      setDeleteModalOpen(false);
    }
  };

  const fields = [
    { fieldName: "orgName", renderType: "text", label: "Organization Name*", isRequired: true },
    {
      fieldName: "domains",
      renderType: "select",
      type: "multi-select",
      label: "Domains*",
      isRequired: true,
      isCreatable: true,
      options: [],
      placeholder: "Add one or more domains",
    },
    { fieldName: "itAdminEmail", renderType: "text", label: "IT Admin Email*", isRequired: true },
  ];

  const additionalButtons = existingSsoData?.saml_exists ? [
    <Tooltip title="Remove SSO integration for this organization" key="deleteSso">
      <Button
        variant="contained"
        onClick={() => setDeleteModalOpen(true)}
        disabled={isDeleting}
        style={{ borderRadius: '6px' }}
        label="Remove SSO"
      >
        Remove SSO
      </Button>
    </Tooltip>
  ] : [];

  const handleSubmit = async (values) => {
    setSubmitting(true);
    setSsoResponse(null);
    
    // Additional client-side validation
    const invalidDomains = values.domains.filter(domain => !validateDomain(domain));
    if (invalidDomains.length > 0) {
      message.error(`Invalid domain format: ${invalidDomains.join(', ')}`);
      setSubmitting(false);
      return;
    }

    if (!validateEmail(values.itAdminEmail)) {
      message.error("Invalid IT Admin email format");
      setSubmitting(false);
      return;
    }

    try {
      const response = await createSsoOrg({
        orgName: values.orgName,
        domains: values.domains,
        itAdminEmail: values.itAdminEmail,
      });
      
      if (response.statusCode === "SUCCESS") {
        setSsoResponse(response.response);
        setExistingSsoData({ saml_exists: true, ...response.response });
        message.success("SSO Organization created successfully!");
      } else {
        message.error(response.message || "Failed to create SSO org.");
      }
    } catch (err) {
      console.error("Error creating SSO org:", err);
      const errorMessage = err.response?.data?.message || err.message || "Error creating SSO org.";
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <CustomContainer containerWidth="60%">
        <div style={{ 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center", 
          minHeight: "200px",
          color: themeMode === "dark" ? "#ccc" : "#333"
        }}>
          Loading SSO configuration...
        </div>
      </CustomContainer>
    );
  }

  // Show restriction message if SSO is not available
  if (!ssoAccessAllowed) {
    return (
      <CustomContainer containerWidth="60%">
        <div style={{ marginBottom: 32 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              marginBottom: 1,
              color: currentTheme.text.primary,
              fontSize: 28,
              letterSpacing: 0.5,
            }}
          >
            SSO (SAML) Setup
          </Typography>
          
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            marginBottom: 24,
            padding: "16px",
            background: themeMode === "dark" ? "#2a1f4a" : "#fff3cd",
            borderRadius: "8px",
            border: "1px solid",
            borderColor: themeMode === "dark" ? "#4a3f6a" : "#ffeaa7"
          }}>
            <div style={{ 
              width: "12px", 
              height: "12px", 
              borderRadius: "50%", 
              backgroundColor: "#faad14",
              marginRight: "12px"
            }} />
            <div style={{ color: themeMode === "dark" ? "#ccc" : "#856404" }}>
              <strong>SSO Feature Not Available</strong>
            </div>
          </div>

          <div style={{ 
            padding: "24px",
            background: themeMode === "dark" ? "#18123a" : "#f8f9fa",
            borderRadius: "8px",
            border: "1px solid",
            borderColor: themeMode === "dark" ? "#302d4c" : "#dee2e6"
          }}>
            <h3 style={{ 
              marginBottom: 16, 
              color: themeMode === "dark" ? "#fff" : "#333",
              fontSize: "1.25rem"
            }}>
              Upgrade Required
            </h3>
            
            <p style={{ 
              marginBottom: 16,
              color: themeMode === "dark" ? "#ccc" : "#666",
              lineHeight: 1.6
            }}>
              SSO (SAML) integration is only available for customers on <strong>Scale</strong> or <strong>Enterprise</strong> plans.
            </p>

            <div style={{ marginBottom: 16 }}>
              <strong style={{ color: themeMode === "dark" ? "#fff" : "#333" }}>Current Plan:</strong>
              <div style={{ 
                color: themeMode === "dark" ? "#ccc" : "#666",
                marginTop: 4,
                padding: "8px 12px",
                background: themeMode === "dark" ? "#2a1f4a" : "#e9ecef",
                borderRadius: "4px",
                display: "inline-block"
              }}>
                {planInfo?.planName || "Unknown Plan"}
              </div>
            </div>

            <div style={{ 
              padding: "16px",
              background: themeMode === "dark" ? "#2a1f4a" : "#e7f3ff",
              borderRadius: "6px",
              border: "1px solid",
              borderColor: themeMode === "dark" ? "#4a3f6a" : "#b3d9ff"
            }}>
              <strong style={{ color: themeMode === "dark" ? "#fff" : "#333" }}>To enable SSO:</strong>
              <ul style={{ 
                margin: "8px 0 0 0", 
                paddingLeft: "20px",
                color: themeMode === "dark" ? "#ccc" : "#666"
              }}>
                <li>Upgrade to a Scale or Enterprise plan</li>
                <li>Contact your administrator or sales team</li>
                <li>Visit the <a 
                  href="/billing?tab=plans" 
                  style={{ color: "#FE4E3A", textDecoration: "none" }}
                >
                  Plans & Limits
                </a> page to view available options</li>
              </ul>
            </div>
          </div>
        </div>
      </CustomContainer>
    );
  }

  // Theme-aware style helpers
  const isDark = themeMode === "dark";
  const cardBg = isDark ? "#18123a" : "#fff";
  const cardBorder = isDark ? "#232347" : "#eaeaea";
  const statusBg = isDark ? "#232347" : "#f5f7fa";
  const statusText = isDark ? "#fff" : "#333";
  const statusSubtle = isDark ? "#aaa" : "#666";
  const linkBg = isDark ? "#232347" : "#f5f7fa";
  const linkText = "#FE4E3A";
  const linkHoverBg = isDark ? "#2a1f4a" : "#e9ecef";
  const labelColor = isDark ? "#aaa" : "#888";
  const idColor = "#FE4E3A";

  return (
    <CustomContainer containerWidth="80%">
      <div style={{ marginBottom: 32 }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            marginBottom: 1,
            color: currentTheme.text.primary,
            fontSize: 28,
            letterSpacing: 0.5,
          }}
        >
          SSO (SAML) Setup
        </Typography>
        
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          marginBottom: 24,
          padding: "12px 16px",
          background: themeMode === "dark" ? "#2a1f4a" : "#f0f8ff",
          borderRadius: "8px",
          border: "1px solid",
          borderColor: themeMode === "dark" ? "#4a3f6a" : "#d6e4ff"
        }}>
          <div style={{ 
            width: "12px", 
            height: "12px", 
            borderRadius: "50%", 
            backgroundColor: existingSsoData?.saml_exists ? "#52c41a" : "#faad14",
            marginRight: "12px"
          }} />
          <div style={{ color: themeMode === "dark" ? "#ccc" : "#333" }}>
            {existingSsoData?.saml_exists 
              ? "SSO Configuration Active - Your organization has an active SSO setup"
              : "SSO Available - Setup your organization's SSO (SAML) integration"
            }
            <div style={{ 
              fontSize: "0.875rem", 
              color: themeMode === "dark" ? "#999" : "#666",
              marginTop: "4px"
            }}>
              Plan: {planInfo?.planName} • SSO Feature Enabled
            </div>
          </div>
        </div>
        
        {!existingSsoData?.saml_exists ? (
          <OForm
            formTitle={null}
            initialValues={formInitialValues}
            fields={fields}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            submitButtonText={submitting ? "Creating SSO Organization..." : "Create SSO Organization"}
            submitting={submitting}
            fieldsPerRow={2}
            additionalButtons={additionalButtons}
          />
        ) : (
          <div style={{ 
            marginBottom: 24, 
            padding: "16px", 
            background: themeMode === "dark" ? "#2a1f4a" : "#f0f8ff", 
            borderRadius: "8px",
            border: "1px solid",
            borderColor: themeMode === "dark" ? "#4a3f6a" : "#d6e4ff"
          }}>
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center",
              marginBottom: "12px"
            }}>
              <strong style={{ color: themeMode === "dark" ? "#fff" : "#333" }}>
                SSO Configuration Exists
              </strong>
              {/* <Button
                variant="outlined"
                onClick={() => {
                  setExistingSsoData({ saml_exists: false });
                  setSsoResponse(null);
                }}
                style={{ borderRadius: '6px' }}
                label="Create New SSO"
              >
                Create New SSO
              </Button> */}
            </div>
            <div style={{ color: themeMode === "dark" ? "#ccc" : "#666", fontSize: "14px" }}>
              Your organization already has an SSO configuration. You can view the current setup below or create a new configuration by clicking the button above.
            </div>
          </div>
        )}
        {(ssoResponse || existingSsoData?.saml_exists) && (
          <div style={{
            background: cardBg,
            borderRadius: 12,
            boxShadow: isDark ? "0 1px 4px #0006" : "0 1px 4px #0001",
            padding: 28,
            marginBottom: 32,
            border: `1px solid ${cardBorder}`
          }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 700,
                fontSize: 22,
                marginBottom: 2.25,
                color: currentTheme.text.primary
              }}
            >
              Current SSO Configuration
            </Typography>
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 15, color: labelColor }}>WorkOS Organization ID:</div>
              <div style={{
                fontFamily: "monospace", color: idColor, fontSize: 16, marginTop: 2
              }}>
                {(ssoResponse || existingSsoData).workos_org_id}
              </div>
            </div>
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 15, color: labelColor }}>SSO Setup Portal Link:</div>
              <a
                href={(ssoResponse || existingSsoData).sso_setup_portal_link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-block",
                  background: linkBg,
                  color: linkText,
                  borderRadius: 6,
                  padding: "8px 12px",
                  fontFamily: "monospace",
                  fontSize: 15,
                  textDecoration: "none",
                  maxWidth: 420,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  border: `1px solid ${isDark ? "#302d4c" : "#eaeaea"}`,
                  transition: "background 0.2s"
                }}
                title={(ssoResponse || existingSsoData).sso_setup_portal_link}
                onMouseOver={e => e.currentTarget.style.background = linkHoverBg}
                onMouseOut={e => e.currentTarget.style.background = linkBg}
              >
                {(ssoResponse || existingSsoData).sso_setup_portal_link}
                <span style={{ marginLeft: 8, fontSize: 16 }}>🔗</span>
              </a>
            </div>
            <div>
              <div style={{ fontSize: 15, color: labelColor }}>Domain Verification Portal Link:</div>
              <a
                href={(ssoResponse || existingSsoData).domain_verification_portal_link}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-block",
                  background: linkBg,
                  color: linkText,
                  borderRadius: 6,
                  padding: "8px 12px",
                  fontFamily: "monospace",
                  fontSize: 15,
                  textDecoration: "none",
                  maxWidth: 420,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  border: `1px solid ${isDark ? "#302d4c" : "#eaeaea"}`,
                  transition: "background 0.2s"
                }}
                title={(ssoResponse || existingSsoData).domain_verification_portal_link}
                onMouseOver={e => e.currentTarget.style.background = linkHoverBg}
                onMouseOut={e => e.currentTarget.style.background = linkBg}
              >
                {(ssoResponse || existingSsoData).domain_verification_portal_link}
                <span style={{ marginLeft: 8, fontSize: 16 }}>🔗</span>
              </a>
            </div>
          </div>
        )}
      </div>
      <Modal
        open={deleteModalOpen}
        onCancel={() => setDeleteModalOpen(false)}
        onOk={handleDeleteOrg}
        okText="Yes, Delete"
        cancelText="Cancel"
        confirmLoading={isDeleting}
        title="Confirm Delete"
      >
        <div>Are you sure you want to delete the SSO Organization for this Customer ID? This action cannot be undone.</div>
      </Modal>
    </CustomContainer>
  );
};

export default SsoSetup; 